import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MapPin, Sparkles, ArrowLeft, Search, Loader2 } from "lucide-react";
import { sceneConfig, SceneType } from "@/hooks/useScenes";
import { useGeoData, GeoState, GeoCity } from "@/hooks/useGeoData";
import { cn } from "@/lib/utils";
import { ScrollArea } from "@/components/ui/scroll-area";

interface SceneSelectorSheetProps {
  isOpen: boolean;
  onClose: () => void;
  country: {
    code: string;
    name: string;
    flag: string;
  } | null;
  onSelectScene: (cityId: string, cityName: string, scene: SceneType) => void;
  autoSelect?: boolean;
}

type SelectionStep = "state" | "city" | "scene";

const SceneSelectorSheet: React.FC<SceneSelectorSheetProps> = ({
  isOpen,
  onClose,
  country,
  onSelectScene,
  autoSelect = false,
}) => {
  const { getStates, getCities, requiresState } = useGeoData();
  
  const [currentStep, setCurrentStep] = useState<SelectionStep>("state");
  const [selectedState, setSelectedState] = useState<GeoState | null>(null);
  const [selectedCity, setSelectedCity] = useState<GeoCity | null>(null);
  
  const [states, setStates] = useState<GeoState[]>([]);
  const [cities, setCities] = useState<GeoCity[]>([]);
  const [citySearch, setCitySearch] = useState("");
  
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Determine if country needs state selection
  const needsStateSelection = useMemo(() => {
    if (!country) return false;
    return requiresState(country.code);
  }, [country, requiresState]);

  // Get state/province label
  const stateLabel = useMemo(() => {
    if (!country) return "State";
    return country.code === "CA" ? "Province" : "State";
  }, [country]);

  // Load cities for country/state - MUST be defined before loadStates
  const loadCities = useCallback(async (stateName?: string) => {
    if (!country) {
      console.error("❌ No country selected");
      return;
    }
    
    setLoadingCities(true);
    setError(null);
    
    try {
      // Use the passed stateName parameter (from handleSelectState) or fall back to selectedState
      const state = stateName || selectedState?.name;
      console.log(`🔄 Loading cities for ${country.name} (${country.code})${state ? `, state/province: "${state}"` : ""}...`);
      
      const cityList = await getCities(
        country.code,
        state || undefined
      );
      
      console.log(`✅ Loaded ${cityList.length} cities for ${country.name}${state ? `, ${state}` : ""}`);
      
      if (cityList.length === 0) {
        console.warn(`⚠️ No cities returned for ${country.name}${state ? `, ${state}` : ""}`);
        setError(`No cities found for ${state ? `${state}, ` : ""}${country.name}`);
      } else {
        setError(null);
      }
      
      setCities(cityList);
    } catch (err) {
      console.error("❌ Error loading cities:", err);
      const errorMsg = err instanceof Error ? err.message : "Failed to load cities";
      setError(errorMsg);
      setCities([]);
    } finally {
      setLoadingCities(false);
    }
  }, [country, selectedState, getCities]);

  // Load states for country - MUST be defined before useEffect that uses it
  // Use ref to avoid circular dependency with loadCities
  const loadCitiesRef = useRef(loadCities);
  loadCitiesRef.current = loadCities;
  
  const loadStates = useCallback(async () => {
    if (!country) return;
    
    setLoadingStates(true);
    setError(null);
    
    try {
      console.log(`🔄 Loading ${stateLabel.toLowerCase()}s for ${country.name}...`);
      const stateList = await getStates(country.code);
      console.log(`✅ Loaded ${stateList.length} ${stateLabel.toLowerCase()}s`);
      setStates(stateList);
      
      if (stateList.length === 0) {
        // No states, go directly to cities - use ref to avoid circular dependency
        setCurrentStep("city");
        loadCitiesRef.current(undefined);
      }
    } catch (err) {
      console.error("❌ Error loading states:", err);
      setError(err instanceof Error ? err.message : "Failed to load states");
    } finally {
      setLoadingStates(false);
    }
  }, [country, getStates, stateLabel]); // Removed loadCities from deps, using ref instead

  // Reset when sheet opens/closes - NOW safe to reference loadStates and loadCities
  useEffect(() => {
    if (!isOpen) {
      setCurrentStep(needsStateSelection ? "state" : "city");
      setSelectedState(null);
      setSelectedCity(null);
      setStates([]);
      setCities([]);
      setCitySearch("");
      setError(null);
    } else if (country) {
      console.log(`📋 Sheet opened for ${country.name} (${country.code}), needsStateSelection: ${needsStateSelection}`);
      // Load states if needed
      if (needsStateSelection) {
        setCurrentStep("state");
        loadStates();
      } else {
        // Skip to city selection
        setCurrentStep("city");
        loadCities(undefined);
      }
    }
  }, [isOpen, country, needsStateSelection, loadStates, loadCities]);

  // Handle state selection
  const handleSelectState = useCallback((state: GeoState) => {
    console.log("📍 State selected:", state);
    setSelectedState(state);
    setCurrentStep("city");
    setCitySearch("");
    setCities([]); // Clear previous cities
    // Load cities for selected state - pass state name directly
    loadCities(state.name);
  }, [loadCities]);

  // Handle city selection
  const handleSelectCity = useCallback((city: GeoCity) => {
    setSelectedCity(city);
    setCurrentStep("scene");
  }, []);

  // Filter cities by search
  const filteredCities = useMemo(() => {
    if (!citySearch.trim()) return cities;
    const searchLower = citySearch.toLowerCase();
    return cities.filter(city => 
      city.name.toLowerCase().includes(searchLower)
    );
  }, [cities, citySearch]);

  // Scene options
  const scenes = useMemo(() => {
    const baseCount = selectedCity ? selectedCity.name.length : 5;
    const sceneTypes: SceneType[] = ["beach", "cafe", "rooftop", "night_city"];
    return sceneTypes.map((scene, index) => ({
      type: scene,
      ...sceneConfig[scene],
      liveCount: Math.max(0, baseCount + (index * 3) - 2),
      isTrending: index === 0,
    }));
  }, [selectedCity?.name]);

  // Handle scene selection
  const handleSceneSelect = useCallback((scene: SceneType) => {
    if (!selectedCity) return;
    const cityId = selectedCity.id || `${country?.code}-${selectedCity.name}`;
    onSelectScene(cityId, selectedCity.name, scene);
    onClose();
  }, [selectedCity, country, onSelectScene, onClose]);

  // Auto-navigate if enabled
  useEffect(() => {
    if (autoSelect && selectedCity && currentStep === "scene" && !loadingCities) {
      const timer = setTimeout(() => {
        handleSceneSelect("beach");
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [autoSelect, selectedCity, currentStep, loadingCities, handleSceneSelect]);

  if (!country) return null;

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="bottom" className="h-[80vh] rounded-t-3xl">
        <SheetHeader className="pb-4">
          <SheetTitle className="flex items-center gap-2">
            {currentStep === "city" && needsStateSelection && selectedState && (
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 -ml-2"
                onClick={() => {
                  setCurrentStep("state");
                  setSelectedCity(null);
                  setCities([]);
                  setCitySearch("");
                }}
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
            )}
            {currentStep === "scene" && (
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 -ml-2"
                onClick={() => {
                  setCurrentStep("city");
                  setSelectedCity(null);
                }}
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
            )}
            <span className="text-2xl">{country.flag}</span>
            <span>{country.name}</span>
          </SheetTitle>
          <SheetDescription>
            {currentStep === "state" && `Choose a ${stateLabel.toLowerCase()}`}
            {currentStep === "city" && "Choose a city"}
            {currentStep === "scene" && "Pick a scene"}
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-6 overflow-y-auto max-h-[calc(80vh-100px)]">
          {/* State Selection Step */}
          {currentStep === "state" && needsStateSelection && (
            <section>
              <h3 className="text-sm font-semibold text-muted-foreground mb-3 flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                {stateLabel}
              </h3>
              
              {loadingStates ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : error ? (
                <div className="text-center py-8 text-destructive">{error}</div>
              ) : states.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No {stateLabel.toLowerCase()}s found
                </div>
              ) : (
                <ScrollArea className="h-[400px]">
                  <div className="space-y-2 pr-4">
                    {states.map((state) => (
                      <button
                        key={state.id}
                        onClick={() => handleSelectState(state)}
                        className={cn(
                          "w-full p-4 rounded-lg border text-left transition-all hover:border-primary/50",
                          selectedState?.id === state.id
                            ? "bg-primary text-primary-foreground border-primary"
                            : "bg-card border-border"
                        )}
                      >
                        <div className="font-medium">{state.name}</div>
                        {state.code && (
                          <div className="text-xs opacity-70 mt-1">{state.code}</div>
                        )}
                      </button>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </section>
          )}

          {/* City Selection Step */}
          {currentStep === "city" && (
            <section>
              <h3 className="text-sm font-semibold text-muted-foreground mb-3 flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                City
                {selectedState && (
                  <span className="text-xs font-normal ml-2">
                    ({selectedState.name})
                  </span>
                )}
              </h3>

              {/* Search Bar */}
              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search cities..."
                  value={citySearch}
                  onChange={(e) => setCitySearch(e.target.value)}
                  className="pl-10"
                />
              </div>

              {loadingCities ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : error ? (
                <div className="text-center py-8 text-destructive">{error}</div>
              ) : filteredCities.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  {citySearch ? `No cities found matching "${citySearch}"` : "No cities found"}
                </div>
              ) : (
                <ScrollArea className="h-[400px]">
                  <div className="space-y-2 pr-4">
                    {filteredCities.map((city) => (
                      <button
                        key={city.id}
                        onClick={() => handleSelectCity(city)}
                        className={cn(
                          "w-full p-4 rounded-lg border text-left transition-all hover:border-primary/50",
                          selectedCity?.id === city.id
                            ? "bg-primary text-primary-foreground border-primary"
                            : "bg-card border-border"
                        )}
                      >
                        <div className="font-medium">{city.name}</div>
                        {city.state_name && (
                          <div className="text-xs opacity-70 mt-1">{city.state_name}</div>
                        )}
                      </button>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </section>
          )}

          {/* Scene Selection Step */}
          {currentStep === "scene" && selectedCity && (
            <section>
              <h3 className="text-sm font-semibold text-muted-foreground mb-3 flex items-center gap-2">
                <Sparkles className="w-4 h-4" />
                Pick a Scene in {selectedCity.name}
              </h3>
              <div className="grid grid-cols-2 gap-3">
                {scenes.map((scene) => (
                  <button
                    key={scene.type}
                    onClick={() => handleSceneSelect(scene.type)}
                    className={cn(
                      "relative overflow-hidden rounded-xl p-4 text-left transition-all hover:scale-[1.02]",
                      "bg-gradient-to-br text-white shadow-lg",
                      scene.gradient
                    )}
                  >
                    {scene.isTrending && (
                      <div className="absolute top-2 right-2 px-2 py-0.5 bg-white/20 rounded-full text-xs font-medium backdrop-blur-sm">
                        🔥 Hot
                      </div>
                    )}
                    <span className="text-3xl block mb-2">{scene.emoji}</span>
                    <p className="font-bold">{scene.label}</p>
                    <div className="flex items-center gap-2 mt-1 text-sm text-white/80">
                      {scene.liveCount > 0 ? (
                        <>
                          <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-white" />
                          </span>
                          <span>{scene.liveCount} live</span>
                        </>
                      ) : (
                        <span>Be first!</span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </section>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default SceneSelectorSheet;
